import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import { taskTemplateSchema } from "@/components/Forms/schema/taskTemplateSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "taskTemplate",
    attributesSchema: taskTemplateSchema,
    documentMetaSchema: dtMetaSchema,
});

export type TemplatesDocument = ReturnType<typeof resourceSelector>;
export type MrtTemplate = TemplatesDocument["data"][number];

type Props = {
    isQueryEnabled?: boolean;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtTemplates = ({
    isQueryEnabled = true,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<TemplatesDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: [
            "task-templates",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                sorting: sorting ?? [],
            };

            const response = await fetch(apiUrl("/task-template"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: resourceSelector,
    });
};
